/**
 * WinningProposalsCard — new Client Profile card for tracking won pursuits
 * with attached award notices / signed proposal packages.
 *
 * v3.11.26 — full CRUD wired against `/api/knowledge-base/winning-proposals`.
 * Multiple file attachments per proposal via the unified
 * `AttachmentManager` widget. Linkage to Pursuit Manage Documents
 * (the "promote on Mark as Won" flow) is tracked as a follow-up.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Trophy, Plus, Calendar, Building, Target, Pencil, Trash2, FileText, Download, Loader2 } from 'lucide-react';
import KbDocumentsDropzone from './KbDocumentsDropzone';
import ExtractionStatusBadge from './ExtractionStatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import AttachmentManager from './AttachmentManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  if (v == null) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

const formatYear = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.getFullYear();
};

const emptyForm = {
  title: '',
  award_date: '',
  contract_value: '',
  contract_vehicle: '',
  naics_code: '',
  agency: '',
  notes: '',
  source_pursuit_id: '',
};

export default function WinningProposalsCard({ organizationId }) {
  const { authToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Detail panel state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailAttachments, setDetailAttachments] = useState([]);
  const [detailAttachmentsLoading, setDetailAttachmentsLoading] = useState(false);

  const headers = {
    Authorization: `Bearer ${authToken}`,
    'Content-Type': 'application/json',
  };

  const fetchItems = async () => {
    if (!organizationId || !authToken) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/winning-proposals?organization_id=${organizationId}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
      }
    } catch {
      // soft-fail; keep last known list
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, authToken]);

  // v3.19.0 — Documents-First: poll while any row is extracting.
  useEffect(() => {
    const anyExtracting = items.some(
      (r) => r.extraction_status === 'pending' || r.extraction_status === 'running'
    );
    if (!anyExtracting) return undefined;
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      if (count > 30) {
        clearInterval(id);
        return;
      }
      fetchItems();
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      title: item.title || '',
      award_date: item.award_date || '',
      contract_value: item.contract_value ?? '',
      contract_vehicle: item.contract_vehicle || '',
      naics_code: item.naics_code || '',
      agency: item.agency || '',
      notes: item.notes || '',
      source_pursuit_id: item.source_pursuit_id || '',
    });
    setEditingId(item.id);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        contract_value: form.contract_value === '' ? null : Number(form.contract_value),
      };
      // The backend rejects empty optional strings cleaner if we send null.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null;
      });

      const url = editingId
        ? `${API_URL}/api/knowledge-base/winning-proposals/${editingId}`
        : `${API_URL}/api/knowledge-base/winning-proposals`;
      const method = editingId ? 'PUT' : 'POST';
      const body = editingId
        ? JSON.stringify(payload)
        : JSON.stringify({ ...payload, organization_id: organizationId });

      const res = await fetch(url, { method, headers, body });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Save failed');
      }
      toast.success(editingId ? 'Winning proposal updated' : 'Winning proposal added');
      setModalOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Open detail panel — fetch attachments on open
  const handleOpenDetail = async (item) => {
    setDetailItem(item);
    setDetailOpen(true);
    setDetailAttachments([]);
    setDetailAttachmentsLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/winning-proposals/${item.id}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setDetailAttachments(data.items || []);
      }
    } catch (err) {
      console.error('Failed to load WP attachments:', err);
    } finally {
      setDetailAttachmentsLoading(false);
    }
  };

  // Download an attachment from GridFS
  const downloadAttachment = async (att) => {
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/attachments/file/${att.id}/download`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      toast.error('Download failed: ' + err.message);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?\n\nThis also removes all its attachments.`)) return;
    try {
      const res = await fetch(
        `${API_URL}/api/knowledge-base/winning-proposals/${item.id}`,
        { method: 'DELETE', headers }
      );
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Removed');
      fetchItems();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  return (
    <>
      <Card className="border-slate-200 bg-white shadow-sm" data-testid="winning-proposals-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle
              className="text-lg font-semibold text-slate-900 flex items-center gap-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <Trophy className="w-5 h-5 text-amber-600" />
              Winning Proposals ({items.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={openCreate}
              data-testid="add-winning-proposal-btn"
              className="text-xs h-8"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Manual
            </Button>
          </div>
          <div className="mt-3">
            <KbDocumentsDropzone
              entityType="winning-proposals"
              organizationId={organizationId}
              onUploaded={() => fetchItems()}
              label="Drop winning proposal files — AI will fill in agency, value, NAICS, award date"
              testIdPrefix="wp-dropzone"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <p className="text-sm text-slate-500 text-center py-4">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              No winning proposals recorded. Click <span className="font-semibold">Add</span> to register an award.
            </p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {items.map((wp) => (
              <div
                key={wp.id}
                className="p-4 bg-amber-50/40 hover:bg-amber-50 rounded-lg border border-amber-100 cursor-pointer transition-colors"
                data-testid={`winning-proposal-row-${wp.id}`}
                onClick={() => handleOpenDetail(wp)}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-slate-900 text-sm flex items-center gap-2">
                      <span className="truncate">{wp.title}</span>
                      <ExtractionStatusBadge status={wp.extraction_status} error={wp.extraction_error} />
                    </h4>
                    {wp.agency && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Building className="w-3 h-3" />
                        {wp.agency}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <AttachmentManager itemType="winning-proposals" itemId={wp.id} />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600"
                      onClick={() => openEdit(wp)}
                      data-testid={`edit-winning-proposal-${wp.id}`}
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
                      onClick={() => handleDelete(wp)}
                      data-testid={`delete-winning-proposal-${wp.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                  {wp.contract_value != null && (
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {formatCurrency(wp.contract_value)}
                    </span>
                  )}
                  {formatYear(wp.award_date) && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Awarded {formatYear(wp.award_date)}
                    </span>
                  )}
                  {wp.contract_vehicle && (
                    <Badge variant="outline" className="text-xs border-slate-200 text-slate-600">
                      {wp.contract_vehicle}
                    </Badge>
                  )}
                  {wp.naics_code && (
                    <Badge variant="outline" className="text-xs border-blue-200 bg-blue-50 text-blue-700">
                      NAICS {wp.naics_code}
                    </Badge>
                  )}
                </div>
                {wp.notes && (
                  <p className="text-xs text-slate-600 mt-2 italic">{wp.notes}</p>
                )}
              </div>
            ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[600px]" data-testid="winning-proposal-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Trophy className="w-5 h-5 text-amber-600" />
              {editingId ? 'Edit Winning Proposal' : 'Add Winning Proposal'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="wp-title">Title *</Label>
              <Input
                id="wp-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Veterans Affairs PMO 2025 Award"
                data-testid="wp-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="wp-agency">Agency</Label>
                <Input
                  id="wp-agency"
                  value={form.agency}
                  onChange={(e) => setForm({ ...form, agency: e.target.value })}
                  placeholder="Department of Veterans Affairs"
                />
              </div>
              <div>
                <Label htmlFor="wp-award-date">Award Date</Label>
                <Input
                  id="wp-award-date"
                  type="date"
                  value={form.award_date}
                  onChange={(e) => setForm({ ...form, award_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="wp-contract-value">Contract Value (USD)</Label>
                <Input
                  id="wp-contract-value"
                  type="number"
                  value={form.contract_value}
                  onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
                  placeholder="2500000"
                />
              </div>
              <div>
                <Label htmlFor="wp-vehicle">Contract Vehicle</Label>
                <Input
                  id="wp-vehicle"
                  value={form.contract_vehicle}
                  onChange={(e) => setForm({ ...form, contract_vehicle: e.target.value })}
                  placeholder="GSA MAS / 8(a) STARS III / OASIS+"
                />
              </div>
              <div>
                <Label htmlFor="wp-naics">NAICS Code</Label>
                <Input
                  id="wp-naics"
                  value={form.naics_code}
                  onChange={(e) => setForm({ ...form, naics_code: e.target.value })}
                  placeholder="541512"
                />
              </div>
              <div>
                <Label htmlFor="wp-source">Source Pursuit ID (optional)</Label>
                <Input
                  id="wp-source"
                  value={form.source_pursuit_id}
                  onChange={(e) => setForm({ ...form, source_pursuit_id: e.target.value })}
                  placeholder="UUID of the won pursuit"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="wp-notes">Notes</Label>
              <Input
                id="wp-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional context — e.g. teaming partners, win themes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="save-winning-proposal-btn"
            >
              {saving ? 'Saving…' : editingId ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Winning Proposal Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Trophy className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <span className="truncate">{detailItem?.title || 'Winning Proposal'}</span>
            </DialogTitle>
          </DialogHeader>

          {detailItem && (
            <div className="space-y-5 py-2">
              {/* Header metrics */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {detailItem.agency && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Agency</p>
                    <p className="text-sm font-medium text-slate-900">{detailItem.agency}</p>
                  </div>
                )}
                {detailItem.award_date && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Award Date</p>
                    <p className="text-sm text-slate-900">
                      {new Date(detailItem.award_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                )}
                {detailItem.contract_value != null && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Contract Value</p>
                    <p className="text-sm font-medium text-slate-900">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(detailItem.contract_value)}
                    </p>
                  </div>
                )}
                {detailItem.contract_vehicle && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">Contract Vehicle</p>
                    <p className="text-sm text-slate-900">{detailItem.contract_vehicle}</p>
                  </div>
                )}
                {detailItem.naics_code && (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-0.5">NAICS Code</p>
                    <p className="text-sm font-mono text-slate-900">{detailItem.naics_code}</p>
                  </div>
                )}
              </div>

              {detailItem.notes && (
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{detailItem.notes}</p>
                </div>
              )}

              {/* Uploaded files */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Uploaded Files</p>
                {detailAttachmentsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading files…
                  </div>
                ) : detailAttachments.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No files uploaded yet</p>
                ) : (
                  <div className="space-y-2">
                    {detailAttachments.map((att) => (
                      <div key={att.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <FileText className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{att.filename}</p>
                          {att.file_size && (
                            <p className="text-xs text-slate-400">
                              {att.file_size > 1048576
                                ? `${(att.file_size / 1048576).toFixed(1)} MB`
                                : `${Math.round(att.file_size / 1024)} KB`}
                              {att.content_type && ` · ${att.content_type}`}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="shrink-0 h-8 gap-1.5 text-xs"
                          onClick={() => downloadAttachment(att)}
                          title="Download file"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setDetailOpen(false);
                openEdit(detailItem);
              }}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
